/**
 * useGroups Hook
 *
 * React hook for the DB-first groups system. Wraps groupsService with
 * state management. Components consume this — never the service directly.
 *
 * Reads ride useCachedRead ('groups_data_cache'): the last-known bundle paints
 * instantly (group chips render in the same frame as the localStorage
 * connection list), then the DB revalidates in the background. Mutations write
 * the DB first, then update state + cache together via mutate().
 *
 * Provides:
 * - groups: user's group list (presets + custom)
 * - connectionGroups: map of connectionViewId -> group objects
 * - getGroupsForConnection(chatRoomId): group objects for a connection
 * - getDisplayGroups(chatRoomId): groups with pill suppression applied
 * - getConnectionViewId(chatRoomId): sync lookup of connectionViewId from map
 * - resolveConnectionViewId(chatRoomId): async lookup that refetches if the map
 *   is stale (e.g. a just-created manual contact) — await before assigning groups
 * - actions: setConnectionGroups, createCustomGroup, deleteCustomGroup
 */

import { useMemo, useCallback } from 'react';
import {
  fetchGroupsBundle,
  fetchConnectionViews,
  setConnectionGroups as serviceSetConnectionGroups,
  createCustomGroup as serviceCreateCustomGroup,
  deleteCustomGroup as serviceDeleteCustomGroup,
  renameGroup as serviceRenameGroup,
  reorderGroups as serviceReorderGroups,
  deleteGroup as serviceDeleteGroup,
} from '../services/groups/groupsService';
import { getProfileUserId } from '../utils/identityUtils';
import useCachedRead from './useCachedRead';

const GROUPS_CACHE_KEY = 'groups_data_cache';

const useGroups = () => {
  const profileUserId = getProfileUserId();

  const {
    data: bundle,
    loaded,
    error,
    refresh,
    mutate,
  } = useCachedRead(GROUPS_CACHE_KEY, () => fetchGroupsBundle(getProfileUserId()), {
    enabled: !!profileUserId,
  });

  const groups = useMemo(() => bundle?.groups || [], [bundle]);
  const connectionGroups = useMemo(() => bundle?.connectionGroups || {}, [bundle]);
  const connectionViewMap = useMemo(() => {
    const map = {};
    for (const view of bundle?.views || []) {
      map[view.chat_room_id] = view.id;
    }
    return map;
  }, [bundle]);

  // Anonymous users have no groups — not "loading", just empty.
  const loading = !!profileUserId && !loaded;

  /**
   * Get connectionViewId from chatRoomId.
   */
  const getConnectionViewId = useCallback(
    (chatRoomId) => {
      return connectionViewMap[chatRoomId] || null;
    },
    [connectionViewMap]
  );

  /**
   * Resolve a connectionViewId from chatRoomId, refetching once if the map is
   * stale. A just-created manual contact's connection_views row is written
   * fire-and-forget on Quick Add, so it can commit after this hook mounted —
   * leaving the map without it. Fast path returns the cached id; slow path
   * refetches, updates the bundle (state + cache, so chips render), and returns
   * the resolved id. Async — await it before assigning groups.
   */
  const resolveConnectionViewId = useCallback(
    async (chatRoomId) => {
      if (connectionViewMap[chatRoomId]) return connectionViewMap[chatRoomId];

      const userId = getProfileUserId();
      if (!userId) return null;

      const views = await fetchConnectionViews(userId);
      if (bundle) {
        mutate({ ...bundle, views });
      }
      const view = views.find((v) => v.chat_room_id === chatRoomId);
      return view ? view.id : null;
    },
    [connectionViewMap, bundle, mutate]
  );

  /**
   * Get group objects for a connection by chatRoomId.
   */
  const getGroupsForConnection = useCallback(
    (chatRoomId) => {
      const viewId = connectionViewMap[chatRoomId];
      if (!viewId) return [];
      return connectionGroups[viewId] || [];
    },
    [connectionViewMap, connectionGroups]
  );

  /**
   * Get display groups with pill suppression applied.
   * When Close Friends is present, Friends is hidden.
   */
  const getDisplayGroups = useCallback(
    (chatRoomId) => {
      const groupsForConnection = getGroupsForConnection(chatRoomId);
      // A child implies its parent (Close Friends → Friends): show the child, hide
      // the parent. Keyed on parent_group_id, not the name, so a rename is safe.
      const impliedParentIds = new Set(
        groupsForConnection.filter((g) => g.parent_group_id).map((g) => g.parent_group_id)
      );
      return groupsForConnection.filter((g) => !impliedParentIds.has(g.id));
    },
    [getGroupsForConnection]
  );

  /**
   * Replace all groups for a connection. DB first, then state + cache via mutate.
   */
  const setConnectionGroupsAction = useCallback(
    async (connectionViewId, groupIds) => {
      const success = await serviceSetConnectionGroups(connectionViewId, groupIds, groups);
      if (success && bundle) {
        // Rebuild this connection's assignment locally
        const selectedGroups = groups.filter((g) => groupIds.includes(g.id));

        // Mirror the service's parent rule locally, by id (Close Friends → Friends).
        for (const g of groups) {
          if (
            g.parent_group_id &&
            selectedGroups.some((s) => s.id === g.id) &&
            !selectedGroups.some((s) => s.id === g.parent_group_id)
          ) {
            const parent = groups.find((p) => p.id === g.parent_group_id);
            if (parent) selectedGroups.push(parent);
          }
        }

        mutate({
          ...bundle,
          connectionGroups: { ...bundle.connectionGroups, [connectionViewId]: selectedGroups },
        });
      }
      return success;
    },
    [groups, bundle, mutate]
  );

  /**
   * Create a custom group. DB first, then state + cache via mutate.
   */
  const createCustomGroupAction = useCallback(
    async (name) => {
      const userId = getProfileUserId();
      if (!userId) return null;

      // Next sort_order after the highest, so the new group keeps its own place in
      // the reorder / People chip order (detail-pass-3 §8b).
      const nextOrder = groups.reduce((m, g) => Math.max(m, g.sort_order ?? 0), 0) + 1;
      const newGroup = await serviceCreateCustomGroup(userId, name, nextOrder);
      if (newGroup && bundle) {
        mutate({ ...bundle, groups: [...bundle.groups, newGroup] });
      }
      return newGroup;
    },
    [groups, bundle, mutate]
  );

  /** Rename any group (preset or custom). DB first, then local state. */
  const renameGroupAction = useCallback(
    async (groupId, name) => {
      const success = await serviceRenameGroup(groupId, name);
      if (success && bundle) {
        const apply = (list) =>
          list.map((g) => (g.id === groupId ? { ...g, name: name.trim() } : g));
        mutate({
          ...bundle,
          groups: apply(bundle.groups),
          connectionGroups: Object.fromEntries(
            Object.entries(bundle.connectionGroups).map(([v, l]) => [v, apply(l)])
          ),
        });
      }
      return success;
    },
    [bundle, mutate]
  );

  /** Reorder groups by id (sets sort_order = index). DB first, then local state. */
  const reorderGroupsAction = useCallback(
    async (orderedIds) => {
      const success = await serviceReorderGroups(orderedIds);
      if (success && bundle) {
        const order = new Map(orderedIds.map((id, i) => [id, i]));
        const reordered = [...bundle.groups]
          .map((g) => (order.has(g.id) ? { ...g, sort_order: order.get(g.id) } : g))
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        mutate({ ...bundle, groups: reordered });
      }
      return success;
    },
    [bundle, mutate]
  );

  /** Delete any group — preset or custom (detail-pass-3 §8b). The parent FK is ON
   *  DELETE SET NULL, so a deleted parent leaves its child's parent_group_id null. */
  const deleteGroupAction = useCallback(
    async (groupId) => {
      const success = await serviceDeleteGroup(groupId);
      if (success && bundle) {
        const nextConnectionGroups = {};
        for (const [viewId, groupList] of Object.entries(bundle.connectionGroups)) {
          nextConnectionGroups[viewId] = groupList.filter((g) => g.id !== groupId);
        }
        mutate({
          ...bundle,
          // Drop the group; null any child's parent locally to match the FK.
          groups: bundle.groups
            .filter((g) => g.id !== groupId)
            .map((g) => (g.parent_group_id === groupId ? { ...g, parent_group_id: null } : g)),
          connectionGroups: nextConnectionGroups,
        });
      }
      return success;
    },
    [bundle, mutate]
  );

  /**
   * Delete a custom group. DB first, then state + cache via mutate
   * (removes the group and strips it from all connection assignments).
   */
  const deleteCustomGroupAction = useCallback(
    async (groupId) => {
      const success = await serviceDeleteCustomGroup(groupId);
      if (success && bundle) {
        const nextConnectionGroups = {};
        for (const [viewId, groupList] of Object.entries(bundle.connectionGroups)) {
          nextConnectionGroups[viewId] = groupList.filter((g) => g.id !== groupId);
        }
        mutate({
          ...bundle,
          groups: bundle.groups.filter((g) => g.id !== groupId),
          connectionGroups: nextConnectionGroups,
        });
      }
      return success;
    },
    [bundle, mutate]
  );

  /**
   * Format display groups into pill text. Shared by detail pages.
   */
  const getGroupPillText = useCallback(
    (chatRoomId) => {
      const displayGroups = getDisplayGroups(chatRoomId);
      if (displayGroups.length === 0) return 'Add groups';
      if (displayGroups.length <= 2) return displayGroups.map((g) => g.name).join(', ');
      return `${displayGroups[0].name}, ${displayGroups[1].name} +${displayGroups.length - 2}`;
    },
    [getDisplayGroups]
  );

  return {
    groups,
    connectionGroups,
    loading,
    error,
    getConnectionViewId,
    resolveConnectionViewId,
    getGroupsForConnection,
    getDisplayGroups,
    getGroupPillText,
    refresh,
    actions: {
      setConnectionGroups: setConnectionGroupsAction,
      createCustomGroup: createCustomGroupAction,
      deleteCustomGroup: deleteCustomGroupAction,
      renameGroup: renameGroupAction,
      reorderGroups: reorderGroupsAction,
      deleteGroup: deleteGroupAction,
    },
  };
};

export default useGroups;
